import {useScreenTheme} from '@shared/hooks/useScreenTheme';
import React, {useMemo, useRef, useState} from 'react';
import {
  View,
  Text,
  Image,
  Dimensions,
  Platform,
  StatusBar,
  TouchableOpacity,
  TouchableWithoutFeedback,
  Modal,
  useWindowDimensions,
} from 'react-native';
import {createProfileStyles} from './Profile.styles';
import {ProfileProps} from './ProfileProps';
import {Dropdown, DropdownOption, TextTooltip} from '../ui';
import {profileOptions} from '@shared/constants/global';
import DropdownTickMark from '@shared/assets/icons/DropdownTickMark';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '@shared/store';
import {LogoutIcon} from '@shared/assets/icons/LogoutIcon';
import {useTranslation} from 'react-i18next';
import DotIcon from '@shared/assets/icons/DotIcon';
import {navigate} from '@shared/navigation/navigationRef';
import {setClientTheme} from '@shared/store/themeSlice';
import type {ClientThemeId} from '@shared/theme/clientThemes';

/** Available brand themes with their display swatch colours */
const THEME_SWATCHES: {id: ClientThemeId; color: string; label: string}[] = [
  {id: 'blue',   color: '#005FCC', label: 'Blue'},
  {id: 'teal',   color: '#0F766E', label: 'Teal'},
  {id: 'slate',  color: '#475569', label: 'Slate'},
  {id: 'orange', color: '#CC5500', label: 'Orange'},
  {id: 'purple', color: '#6A1B9A', label: 'Purple'},
];

const ProfilePicture = ({
  name,
  image,
  online = false,
  containerStyle,
  fromHeader,
  onLogout,
  textColor,
  testID,
}: ProfileProps) => {
  const theme = useScreenTheme('profile');
  const styles = useMemo(() => createProfileStyles(theme), [theme]);
  const dispatch = useDispatch();
  const activeTheme = useSelector((state: RootState) => state.theme?.themeName ?? 'blue');
  const profileRef = React.useRef<View>(null);
  // Web: ref to the trigger button so focus can be restored when dropdown closes
  const buttonRef = useRef<any>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number | undefined;
    right: number | undefined;
    width: number;
  }>({
    top: 0,
    left: undefined,
    right: undefined,
    width: 0,
  });
  const [selectedSort, setSelectedSort] = useState<DropdownOption>(
    profileOptions[0],
  );
  const [selectedFilter, setSlectedFilter] = useState<DropdownOption>();
  const {selectedTechnician} = useSelector(
    (state: RootState) => state.technician,
  );
  const {t} = useTranslation();
  const {shiftStarted} = useSelector(
    (state: RootState) => state.shiftManagement,
  );
  const {width} = useWindowDimensions();
  const [isNameTooltipVisible, setIsNameTooltipVisible] = useState(false);
  const fullName = selectedTechnician?.selectedTechnicianName ?? '';
  // Dynamic char limit based on viewport width; minimum 20 chars.
  const maxNameChars = Math.max(20, Math.floor(width / 60));
  const displayFullName =
    fullName && fullName.length > maxNameChars
      ? `${fullName.substring(0, maxNameChars)}...`
      : fullName;

  const measureDropdown = () => {
    if (profileRef && profileRef.current) {
      profileRef.current.measureInWindow(
        (x: number, y: number, width: number, height: number) => {
          const {width: windowWidth, height: windowHeight} =
            Dimensions.get('window');
          const statusBarHeight =
            Platform.OS === 'android' ? StatusBar.currentHeight || 0 : 0;
          const usableHeight = windowHeight - statusBarHeight;
          const usableWidth = windowWidth;

          const ITEM_HEIGHT = 50;
          const GAP = 6;
          const MAX_DROPDOWN_HEIGHT = 200;

          const dropdownHeight = Math.min(
            profileOptions.length * ITEM_HEIGHT,
            MAX_DROPDOWN_HEIGHT,
          );

          const spaceBelow = usableHeight - (y + height);
          const spaceAbove = y - statusBarHeight;

          let top;
          console.log(
            spaceBelow - 10 >= dropdownHeight + GAP,
            spaceAbove >= dropdownHeight + GAP,
            spaceBelow >= spaceAbove,
          );

          if (spaceBelow - 10 >= dropdownHeight + GAP) {
            top = y + height + GAP;
          } else if (spaceAbove >= dropdownHeight + GAP) {
            top =
              spaceBelow >= dropdownHeight + GAP
                ? y - dropdownHeight - 15
                : y - dropdownHeight - 5;
          } else if (spaceBelow >= spaceAbove) {
            top = y + height + GAP;
          } else {
            top = Math.max(statusBarHeight + GAP, y - dropdownHeight - GAP);
          }

          let left, right;
          if (x > usableWidth / 2) {
            right = usableWidth - (x + width);
          } else {
            left = x;
          }

          setDropdownPos({
            top,
            left,
            right,
            width,
          });
        },
      );
    }
  };

  const handleOrientationChange = () => {
    if (isOpen) {
      measureDropdown();
    }
  };

  const handleSelect = (option: DropdownOption) => {
    setSlectedFilter(option);
    setIsOpen(false);
  };

  /**
   * @function logout
   * @description Navigates the user to the agency selection screen for logging out.
   */
  const logOut = () => {
    setIsOpen(false);
    onLogout?.();
  };

  const handleToggleOpen = () => {
    measureDropdown();
    setIsOpen(prev => !prev);
  };

  const handleClose = () => {
    setIsOpen(false);
    // Restore focus to the trigger button on web so Tab order is maintained
    if (Platform.OS === 'web' && buttonRef.current) {
      buttonRef.current.focus?.();
    }
  };

  return fromHeader ? (
    <>
      <TouchableOpacity
        testID={testID ?? 'headerProfileButton'}
        accessible={true}
        accessibilityRole="button"
        accessibilityLabel={`${t('header.accessibility.profileMenuButton')} ${
          selectedTechnician?.selectedTechnicianName
        }`}
        accessibilityHint={t('header.accessibility.profileMenuHint')}
        accessibilityState={{expanded: isOpen}}
        style={[styles.profileContainer, containerStyle]}
        onPress={handleToggleOpen}
        focusable={true}
        // Web keyboard accessibility: tabIndex ensures element stays in tab order;
        // onKeyDown handles Enter/Space activation (keypress does not fire for Space in browsers)
        {...(Platform.OS === 'web'
          ? {
              tabIndex: 0,
              onKeyDown: (e: any) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggleOpen();
                }
              },
            }
          : {})}
        ref={(node: any) => {
          (profileRef as any).current = node;
          buttonRef.current = node;
        }}>
        {/* Avatar Circle */}
        <View style={styles.avatar}>
          {image ? (
            <Image
              source={image}
              style={styles.imageContainer}
              resizeMode="cover"
            />
          ) : (
            <Text
              style={styles.text}
              testID="profileAvatarInitials"
              accessibilityLabel={`${t(
                'header.accessibility.profileInitials',
              )} ${name}`}>
              {name}
            </Text>
          )}
        </View>

        {/* Online Indicator */}
        {online && <View style={styles.onlineDot} />}
      </TouchableOpacity>
      {isOpen && (
        <Modal
          testID="profileDropdownModal"
          accessibilityViewIsModal
          transparent
          visible
          animationType="none"
          supportedOrientations={[
            'portrait',
            'landscape',
            'landscape-left',
            'landscape-right',
          ]}
          onOrientationChange={handleOrientationChange}>
          {/* Overlay */}
          <TouchableWithoutFeedback onPress={handleClose}>
            <View testID="profileOverlay" style={styles.modalOverlay} />
          </TouchableWithoutFeedback>

          {/* Dropdown */}
          <View
            testID="profileDropdownMenu"
            accessibilityRole="menu"
            accessibilityLabel={t('header.accessibility.profileOptionsMenu')}
            style={[
              styles.dropdownMenu,
              {
                top: dropdownPos.top,
                left: dropdownPos.left,
                right: dropdownPos.right,
                minWidth: dropdownPos.width,
              },
            ]}
            // Web: close dropdown on Escape key
            {...(Platform.OS === 'web'
              ? {
                  onKeyDown: (e: any) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      handleClose();
                    }
                  },
                }
              : {})}>
            <View style={styles.profileSubContainer}>
              <View style={[styles.profileSubChildContainer]}>
                {/* Avatar Circle */}
                <View style={styles.dropdownAvatar}>
                  {image ? (
                    <Image
                      source={image}
                      style={styles.imageContainer}
                      resizeMode="cover"
                    />
                  ) : (
                    <Text style={styles.text}>{name}</Text>
                  )}
                </View>
                <View style={styles.profileSubInnerContainer}>
                  <TextTooltip
                    tooltipText={fullName}
                    disabled={fullName.length <= maxNameChars}
                    placement="above"
                    onVisibilityChange={setIsNameTooltipVisible}
                    tooltipHorizontalAlign='right'>
                    <Text
                      style={styles.dropdownUserName}
                      testID="profileUserName"
                      accessibilityRole="text"
                      accessibilityLabel={`${t(
                        'header.accessibility.profileUserName',
                      )} ${displayFullName}`}>
                      {displayFullName}
                    </Text>
                  </TextTooltip>
                  <Text
                    style={styles.dropdownUserId}
                    testID="profileUserId"
                    accessibilityRole="text"
                    accessibilityLabel={`${t(
                      'header.accessibility.profileTechnicianId',
                    )} T-${selectedTechnician?.selectedTechnicianId}`}
                    {...(Platform.OS === 'web' ? {tabIndex: 0} : {})}>
                    {`T-${selectedTechnician?.selectedTechnicianId}`}
                  </Text>
                  <View
                    style={styles.onLine}
                    accessible={true}
                    accessibilityRole="text"
                    accessibilityLabel={`${t('dashboard.status')} ${
                      shiftStarted
                        ? t('header.online')
                        : t('header.offline')
                    }`}
                    focusable={true}
                    {...(Platform.OS === 'web' ? {tabIndex: 0} : {})}>
                    <DotIcon
                      color={shiftStarted ? theme.online : theme.offline}
                    />
                    <Text
                      style={
                        shiftStarted ? styles.onlineText : styles.offlineText
                      }
                      accessibilityElementsHidden={true}
                      importantForAccessibility="no-hide-descendants">
                      {shiftStarted
                        ? `${t('header.online')}`
                        : `${t('header.offline')}`}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
            {/* <View style={styles.separatorLine} />

            <View style={styles.menuItem}>
              {profileOptions.map(item => {
                let IconComponent = item?.icon;
                return (
                  <TouchableOpacity
                    key={item.value.toString()}
                    testID={`profileMenuItem-${item.value}`}
                    accessible
                    accessibilityRole="menuitem"
                    accessibilityLabel={t(item.text)}
                    accessibilityHint={t(
                      'header.accessibility.profileMenuItemHint',
                    )}
                    style={[
                      styles.menuSubItem,
                      // selectedSort?.value === item.value && styles.activeItem,
                    ]}
                    onPress={() => handleSelect(item)}>
                    <IconComponent color={theme.dropdownValuesIcon} />
                    <Text
                      style={[
                        styles.menuItemText,
                        // selectedSort?.value === item.value && styles.activeItemText,
                      ]}>
                      {t(item.text)}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View> */}
            {/* <View style={styles.separatorLine} /> */}

            {/* ── Theme Colour Picker ─────────────────────────────── */}
            {/* <View style={styles.themePickerContainer}>
              <Text style={styles.themePickerLabel}>Theme</Text>
              <View style={styles.themeSwatchRow}>
                {THEME_SWATCHES.map(swatch => (
                  <TouchableOpacity
                    key={swatch.id}
                    testID={`themeSwatchButton-${swatch.id}`}
                    accessible
                    accessibilityRole="button"
                    accessibilityLabel={`${swatch.label} theme`}
                    accessibilityState={{selected: activeTheme === swatch.id}}
                    onPress={() => dispatch(setClientTheme(swatch.id))}
                    style={[
                      styles.themeSwatch,
                      {backgroundColor: swatch.color},
                      activeTheme === swatch.id && styles.themeSwatchActive,
                    ]}
                  />
                ))}
              </View>
            </View>  */}

            <View style={styles.separatorLine} />
            <TouchableOpacity
              testID="profileLogoutButton"
              accessible
              accessibilityRole="button"
              accessibilityLabel={t('header.logOut')}
              accessibilityHint={t('header.accessibility.profileLogoutHint')}
              style={[
                styles.logOut,
                // selectedSort?.value === item.value && styles.activeItem,
              ]}
              onPress={() => logOut()}>
              <LogoutIcon color={theme.logout} />
              <Text
                style={[
                  styles.logOutText,
                  // selectedSort?.value === item.value && styles.activeItemText,
                ]}>
                {t('header.logOut')}
              </Text>
            </TouchableOpacity>
          </View>
        </Modal>
      )}
    </>
  ) : (
    <View
      style={[styles.profileContainer, containerStyle]}
      testID="ProfileIdContainer">
      {/* Avatar Circle */}
      <View style={styles.avatar}>
        {image ? (
          <Image
            source={image}
            style={styles.imageContainer}
            resizeMode="cover"
          />
        ) : (
          <Text
            style={[styles.text, textColor ? {color: textColor} : undefined]}>
            {name}
          </Text>
        )}
      </View>

      {/* Online Indicator */}
      {online && <View style={styles.onlineDot} />}
    </View>
  );
};

export default ProfilePicture;
